/**
 * V43 + V43.1: 휴가현황 수동 수정 모달
 *
 * HR/Admin이 특정 직원의 휴가 기준값을 수동으로 수정할 수 있는 모달입니다.
 *
 * **V43 - 기준값 수정:**
 * - annual.granted (발생)
 * - annual.validFrom (적용시작)
 * - annual.validUntil (적용종료)
 * - annual.carryOver (이월)
 * - annual.preUsed (선사용 연차)
 *
 * **V43.1 - 월별 사용량 조정:**
 * - 특정 월의 사용량을 직접 조정
 * - leaveEntitlements 원장 조정 + syncLeaveBalance 흐름 유지
 *
 * **읽기 전용 필드:**
 * - annual.used (사용)
 * - annual.remaining (잔여)
 * - annual.negative (마이너스 연차)
 *
 * **저장 시 동작:**
 * 1. BE에서 기준값/사용량 업데이트
 * 2. syncLeaveBalance()로 used/remaining 재계산
 * 3. 감사 로그 기록
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, CheckCircle, Loader, Calendar, Settings } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';
import { LeaveBalance } from '../../../shared/types/leaveBalance';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
// V65-FIX(2026-04-24): 수정 팝업 월별 사용량 1월 몰림 해결 — 정상 화면과 동일 경로
// (balance.annual.monthlyUsage 우선 + useLeaveMonthlyUsage histories fallback)
import { useLeaveMonthlyUsage } from '../../../shared/hooks/useLeaveMonthlyUsage';

// V43.1: 월별 사용량 데이터
interface MonthlyUsageData {
  month: string; // YYYY-MM
  usedDays: number;
  historyCount: number;
}

interface LeaveBalanceEditModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 모달 닫기 콜백 */
  onClose: () => void;
  /** 대상 직원 ID */
  employeeId: string;
  /** 대상 직원 이름 */
  employeeName: string;
  /** 대상 직원 사번 */
  employeeNumber?: string;
  /** 기존 휴가 잔액 데이터 */
  balance: LeaveBalance | null;
  /** 저장 성공 콜백 (목록 갱신용) */
  onSaveSuccess: () => void;
}

interface FormData {
  granted: number;
  validFrom: string;
  validUntil: string;
  carryOver: number;
  preUsed: number;
  reason: string;
}

// V43.1: 월별 조정 폼
interface MonthlyAdjustFormData {
  targetMonth: string;
  targetUsedDays: number;
  reason: string;
}

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';
type TabType = 'base' | 'monthly'; // V43.1: 탭 타입

/**
 * V43: 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 */
function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * V43: 연도 시작일 반환 (YYYY-01-01)
 */
function getYearStartString(year?: number): string {
  const y = year || new Date().getFullYear();
  return `${y}-01-01`;
}

/**
 * V43: 연도 종료일 반환 (YYYY-12-31)
 */
function getYearEndString(year?: number): string {
  const y = year || new Date().getFullYear();
  return `${y}-12-31`;
}

/**
 * V43.1: 현재 월을 YYYY-MM 형식으로 반환
 */
function getCurrentMonthString(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}

export function LeaveBalanceEditModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  employeeNumber,
  balance,
  onSaveSuccess,
}: LeaveBalanceEditModalProps) {
  const overlayProps = useModalOverlayClose(onClose);

  // V43.1: 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('base');

  // 폼 상태 (기준값 수정)
  const [formData, setFormData] = useState<FormData>({
    granted: 0,
    validFrom: getYearStartString(),
    validUntil: getYearEndString(),
    carryOver: 0,
    preUsed: 0,
    reason: '',
  });

  // V43.1: 월별 조정 폼 상태
  const [monthlyFormData, setMonthlyFormData] = useState<MonthlyAdjustFormData>({
    targetMonth: getCurrentMonthString(),
    targetUsedDays: 0,
    reason: '',
  });

  // V65-FIX(2026-04-24): 월별 사용량 SSoT 전환
  //   - 기존: BE CF `getEmployeeMonthlyAnnualUsage` 응답 사용 → leaveHistories에서
  //           source='import' 이력의 startDate가 모두 1월에 몰려있어 1월에 전부 쌓여 보이는 버그
  //   - 변경: balance.annual.monthlyUsage 를 SSoT로 하고, 비어있는 월만
  //           useLeaveMonthlyUsage 히스토리 집계로 보완 (정상 화면 3곳과 동일 경로)
  //     참고: MyLeaveStatusTable.tsx:150-168, HrLeaveOverviewPage.tsx:1309-1318,
  //          OrgLeaveManagementPage.tsx:698-708
  const [currentMonthUsed, setCurrentMonthUsed] = useState<number>(0);

  // V65-FIX: histories fallback (정상 화면과 동일)
  const monthlyUsageResult = useLeaveMonthlyUsage(
    employeeId,
    {
      validFrom: balance?.annual?.validFrom,
      validUntil: balance?.annual?.validUntil,
      periodStart: balance?.annual?.periodStart,
      periodEnd: balance?.annual?.periodEnd,
    },
    'annual'
  );

  // V65-FIX: balance(DB) 우선, histories fallback 병합 → MonthlyUsageData[] 유도
  const monthlyUsage = useMemo<MonthlyUsageData[]>(() => {
    const src = (balance?.annual?.monthlyUsage || {}) as Record<string | number, number>;
    const hist = monthlyUsageResult.monthlyUsage || {};
    const year = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const balanceVal = src[m] ?? src[String(m)] ?? src[String(m).padStart(2, '0')];
      const value =
        balanceVal !== undefined && balanceVal !== null
          ? Number(balanceVal) || 0
          : Number(hist[m] ?? 0) || 0;
      return {
        month: `${year}-${String(m).padStart(2, '0')}`,
        usedDays: value,
        historyCount: 0,
      };
    });
  }, [balance, monthlyUsageResult.monthlyUsage]);

  const loadingMonthly = monthlyUsageResult.isLoading;

  // 저장 상태
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // balance 데이터로 폼 초기화
  useEffect(() => {
    if (balance?.annual) {
      setFormData({
        granted: balance.annual.granted ?? 0,
        validFrom: balance.annual.periodStart || balance.annual.validFrom || getYearStartString(),
        validUntil: balance.annual.periodEnd || balance.annual.validUntil || getYearEndString(),
        carryOver: balance.annual.carryOver ?? 0,
        preUsed: balance.annual.preUsed ?? 0,
        reason: '',
      });
    } else {
      // balance가 없으면 기본값
      setFormData({
        granted: 0,
        validFrom: getYearStartString(),
        validUntil: getYearEndString(),
        carryOver: 0,
        preUsed: 0,
        reason: '',
      });
    }
    // V43.1: 월별 조정 폼 초기화
    setMonthlyFormData({
      targetMonth: getCurrentMonthString(),
      targetUsedDays: 0,
      reason: '',
    });
    setActiveTab('base');
    setSaveStatus('idle');
    setErrorMessage('');
    setCurrentMonthUsed(0);
  }, [balance, isOpen]);

  // V65-FIX: CF 호출 제거 — balance + useLeaveMonthlyUsage 훅으로 대체 (위 monthlyUsage useMemo)

  // V43.1: 월 선택 변경 시 해당 월 사용량 업데이트
  useEffect(() => {
    const monthData = monthlyUsage.find(m => m.month === monthlyFormData.targetMonth);
    if (monthData) {
      setCurrentMonthUsed(monthData.usedDays);
      setMonthlyFormData(prev => ({
        ...prev,
        targetUsedDays: monthData.usedDays,
      }));
    } else {
      setCurrentMonthUsed(0);
      setMonthlyFormData(prev => ({
        ...prev,
        targetUsedDays: 0,
      }));
    }
  }, [monthlyFormData.targetMonth, monthlyUsage]);

  // 입력 핸들러
  const handleNumberChange = (field: keyof FormData, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleStringChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 유효성 검증
  const validateForm = (): string | null => {
    // V66: granted 는 정책 자동 산정 — 수기 수정 차단을 위해 UI 제거.
    //   payload 는 BE 요구사항(필수 number) 충족을 위해 기존 DB값을 passthrough.
    if (formData.carryOver < 0) {
      return '이월(carryOver)은 0 이상이어야 합니다.';
    }
    if (formData.preUsed < 0 || formData.preUsed > 5) {
      return '선사용(preUsed)은 0~5 사이여야 합니다.';
    }
    if (!formData.validFrom || !/^\d{4}-\d{2}-\d{2}$/.test(formData.validFrom)) {
      return '적용시작일은 YYYY-MM-DD 형식이어야 합니다.';
    }
    if (!formData.validUntil || !/^\d{4}-\d{2}-\d{2}$/.test(formData.validUntil)) {
      return '적용종료일은 YYYY-MM-DD 형식이어야 합니다.';
    }
    if (formData.validFrom > formData.validUntil) {
      return '적용시작일이 적용종료일보다 늦을 수 없습니다.';
    }
    return null;
  };

  // V66: 기준값 수정 탭 — 원본 balance와 비교한 dirty 여부
  // - 변경된 필드가 하나도 없으면 BE 호출하지 않음 (no-op 저장 차단)
  // - preUsed 비교는 balance.annual.preUsed가 null/undefined인 경우와 0인 경우를
  //   동일하게 취급해야 한다 (폼 초기값 `?? 0` 강제 변환과 일관)
  const isBaseFormDirty = (): boolean => {
    if (!balance?.annual) {
      // 신규 직원(balance 없음) → 무조건 dirty 로 간주 (생성 필요)
      return true;
    }
    const a = balance.annual;
    // V66: granted 는 passthrough 라 dirty 비교에서 제외 — 사용자가 변경할 수 없음
    const origValidFrom = a.periodStart || a.validFrom || '';
    const origValidUntil = a.periodEnd || a.validUntil || '';
    const origCarryOver = Number(a.carryOver ?? 0);
    const origPreUsed = Number(a.preUsed ?? 0);

    if (formData.validFrom !== origValidFrom) return true;
    if (formData.validUntil !== origValidUntil) return true;
    if (Number(formData.carryOver) !== origCarryOver) return true;
    if (Number(formData.preUsed) !== origPreUsed) return true;
    if ((formData.reason || '').trim().length > 0) return true; // 사유만 입력해도 변경으로 인정
    return false;
  };

  // 저장 핸들러 (기준값 수정)
  const handleSave = async () => {
    // 유효성 검증
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setSaveStatus('error');
      return;
    }

    // V66: no-op 저장 차단 (BE 호출 0회 보장)
    if (!isBaseFormDirty()) {
      setErrorMessage('변경 사항이 없습니다.');
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');
    setErrorMessage('');

    try {
      const functions = getFunctions();
      const adminUpdateLeaveAnnual = httpsCallable<
        {
          employeeId: string;
          annual: {
            granted: number;
            validFrom: string;
            validUntil: string;
            carryOver: number;
            preUsed: number;
          };
          reason?: string;
        },
        { success: boolean; employeeId: string; updatedAt: string; recalculated: boolean; noop?: boolean }
      >(functions, 'adminUpdateEmployeeLeaveAnnual');

      const result = await adminUpdateLeaveAnnual({
        employeeId,
        annual: {
          granted: formData.granted,
          validFrom: formData.validFrom,
          validUntil: formData.validUntil,
          carryOver: formData.carryOver,
          preUsed: formData.preUsed,
        },
        reason: formData.reason || undefined,
      });

      if (result.data.success) {
        setSaveStatus('success');
        // 1.5초 후 모달 닫기 및 목록 갱신
        setTimeout(() => {
          onSaveSuccess();
          onClose();
        }, 1500);
      } else {
        throw new Error('저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('[V43] 휴가현황 수정 실패:', error);
      setErrorMessage(error.message || '저장 중 오류가 발생했습니다.');
      setSaveStatus('error');
    }
  };

  // V43.1: 월별 조정 저장 핸들러
  const handleMonthlyAdjustSave = async () => {
    // 유효성 검증
    if (!monthlyFormData.targetMonth || !/^\d{4}-\d{2}$/.test(monthlyFormData.targetMonth)) {
      setErrorMessage('대상 월은 YYYY-MM 형식이어야 합니다.');
      setSaveStatus('error');
      return;
    }

    if (monthlyFormData.targetUsedDays < 0) {
      setErrorMessage('목표 사용일수는 0 이상이어야 합니다.');
      setSaveStatus('error');
      return;
    }

    // 변경 없음 체크
    if (monthlyFormData.targetUsedDays === currentMonthUsed) {
      setErrorMessage('변경 사항이 없습니다.');
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');
    setErrorMessage('');

    try {
      const functions = getFunctions();
      const adjustMonthlyUsage = httpsCallable<
        {
          employeeId: string;
          targetMonth: string;
          targetUsedDays: number;
          reason?: string;
        },
        {
          success: boolean;
          employeeId: string;
          targetMonth: string;
          beforeUsedDays: number;
          afterUsedDays: number;
          adjustedDays: number;
          updatedAt: string;
        }
      >(functions, 'adminAdjustEmployeeAnnualUsageByMonth');

      const result = await adjustMonthlyUsage({
        employeeId,
        targetMonth: monthlyFormData.targetMonth,
        targetUsedDays: monthlyFormData.targetUsedDays,
        reason: monthlyFormData.reason || undefined,
      });

      if (result.data.success) {
        setSaveStatus('success');
        // 1.5초 후 모달 닫기 및 목록 갱신
        setTimeout(() => {
          onSaveSuccess();
          onClose();
        }, 1500);
      } else {
        throw new Error('조정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('[V43.1] 월별 사용량 조정 실패:', error);
      setErrorMessage(error.message || '조정 중 오류가 발생했습니다.');
      setSaveStatus('error');
    }
  };

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            휴가현황 수정
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 직원 정보 */}
        <div
          style={{
            padding: '12px',
            backgroundColor: 'var(--color-gray-50)',
            borderRadius: '6px',
            marginBottom: '16px',
          }}
        >
          <div style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
            {employeeNumber && <span style={{ marginRight: '8px' }}>{employeeNumber}</span>}
            <strong>{employeeName}</strong>
          </div>
        </div>

        {/* V43.1: 탭 네비게이션 */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--color-gray-200)',
            marginBottom: '16px',
          }}
        >
          <button
            onClick={() => { setActiveTab('base'); setSaveStatus('idle'); setErrorMessage(''); }}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'base' ? '600' : '400',
              color: activeTab === 'base' ? 'var(--color-primary)' : 'var(--color-gray-500)',
              borderBottom: activeTab === 'base' ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Settings size={16} />
            기준값 수정
          </button>
          <button
            onClick={() => { setActiveTab('monthly'); setSaveStatus('idle'); setErrorMessage(''); }}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'monthly' ? '600' : '400',
              color: activeTab === 'monthly' ? 'var(--color-primary)' : 'var(--color-gray-500)',
              borderBottom: activeTab === 'monthly' ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Calendar size={16} />
            월별 조정
          </button>
        </div>

        {/* 읽기 전용 필드 (현재 값) */}
        <div
          style={{
            padding: '12px',
            backgroundColor: 'var(--color-blue-50)',
            borderRadius: '6px',
            marginBottom: '20px',
          }}
        >
          <div style={{ fontSize: '12px', color: 'var(--color-blue-600)', marginBottom: '8px' }}>
            현재 값 (자동 계산)
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px' }}>
            <div>
              <span style={{ color: 'var(--color-gray-500)' }}>사용:</span>{' '}
              <strong>{balance?.annual?.used ?? 0}일</strong>
            </div>
            {/* V51: 마이너스 잔여일 때 빨간색 강조 */}
            <div>
              <span style={{ color: 'var(--color-gray-500)' }}>잔여:</span>{' '}
              <strong style={{ color: ((balance?.annual?.remaining ?? 0) < 0) ? 'var(--color-red-600)' : undefined }}>{balance?.annual?.remaining ?? 0}일</strong>
            </div>
            {(balance?.annual?.negative ?? 0) !== 0 && (
              <div>
                <span style={{ color: 'var(--color-gray-500)' }}>마이너스:</span>{' '}
                <strong style={{ color: 'var(--color-red-600)' }}>{balance?.annual?.negative ?? 0}일</strong>
              </div>
            )}
          </div>
        </div>

        {/* ===== 기준값 수정 탭 (V43) ===== */}
        {activeTab === 'base' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* V66: '발생 (일)' 입력란 제거 — 정책 자동 산정 값을 운영자가 수기 수정하지 못하도록 차단.
                granted 는 payload 에서 기존 DB 값을 그대로 passthrough (BE 요구 필수 필드). */}

            {/* 이월 */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                이월 (일)
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.carryOver}
                onChange={e => handleNumberChange('carryOver', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* 선사용 연차 */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                선사용 연차 (일) <span style={{ color: 'var(--color-gray-400)', fontSize: '12px' }}>0~5</span>
              </label>
              <input
                type="number"
                step="1"
                min="0"
                max="5"
                value={formData.preUsed}
                onChange={e => handleNumberChange('preUsed', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* 적용 기간 */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  적용시작일
                </label>
                <input
                  type="date"
                  value={formData.validFrom}
                  onChange={e => handleStringChange('validFrom', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  적용종료일
                </label>
                <input
                  type="date"
                  value={formData.validUntil}
                  onChange={e => handleStringChange('validUntil', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>

            {/* 수정 사유 */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                수정 사유 <span style={{ color: 'var(--color-gray-400)', fontSize: '12px' }}>(선택)</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={e => handleStringChange('reason', e.target.value)}
                placeholder="수정 사유를 입력하세요 (감사 로그에 기록됨)"
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        )}

        {/* ===== 월별 조정 탭 (V43.1) ===== */}
        {activeTab === 'monthly' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 로딩 상태 */}
            {loadingMonthly && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-gray-500)' }}>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                월별 사용량 로딩 중...
              </div>
            )}

            {!loadingMonthly && (
              <>
                {/* 월별 사용량 현황 */}
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--color-gray-50)',
                    borderRadius: '6px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                  }}
                >
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginBottom: '8px', fontWeight: '500' }}>
                    {new Date().getFullYear()}년 월별 사용량
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {monthlyUsage.map(m => (
                      <div
                        key={m.month}
                        onClick={() => setMonthlyFormData(prev => ({ ...prev, targetMonth: m.month }))}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          backgroundColor: m.month === monthlyFormData.targetMonth
                            ? 'var(--color-primary)'
                            : m.usedDays > 0
                              ? 'var(--color-blue-100)'
                              : 'var(--color-gray-100)',
                          color: m.month === monthlyFormData.targetMonth
                            ? 'white'
                            : 'var(--color-gray-700)',
                        }}
                      >
                        {m.month.split('-')[1]}월: {m.usedDays}일
                      </div>
                    ))}
                  </div>
                </div>

                {/* 대상 월 선택 */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                    조정 대상 월
                  </label>
                  <input
                    type="month"
                    value={monthlyFormData.targetMonth}
                    onChange={e => setMonthlyFormData(prev => ({ ...prev, targetMonth: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </div>

                {/* 현재/목표 사용일수 */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                      현재 사용일수
                    </label>
                    <input
                      type="number"
                      value={currentMonthUsed}
                      disabled
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'var(--color-gray-50)',
                        color: 'var(--color-gray-500)',
                      }}
                    />
                  </div>
                  <div style={{ padding: '8px 0', fontSize: '18px', color: 'var(--color-gray-400)' }}>→</div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                      목표 사용일수
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={monthlyFormData.targetUsedDays}
                      onChange={e => setMonthlyFormData(prev => ({
                        ...prev,
                        targetUsedDays: parseFloat(e.target.value) || 0,
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: '4px',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                </div>

                {/* 조정량 표시 */}
                {monthlyFormData.targetUsedDays !== currentMonthUsed && (
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: '6px',
                      backgroundColor: monthlyFormData.targetUsedDays > currentMonthUsed
                        ? 'var(--color-red-50)'
                        : 'var(--color-green-50)',
                      fontSize: '13px',
                      color: monthlyFormData.targetUsedDays > currentMonthUsed
                        ? 'var(--color-red-700)'
                        : 'var(--color-green-700)',
                    }}
                  >
                    {monthlyFormData.targetUsedDays > currentMonthUsed
                      ? `+${(monthlyFormData.targetUsedDays - currentMonthUsed).toFixed(1)}일 (사용량 증가 → 잔여 감소)`
                      : `${(monthlyFormData.targetUsedDays - currentMonthUsed).toFixed(1)}일 (사용량 감소 → 잔여 증가)`}
                  </div>
                )}

                {/* 조정 사유 */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                    조정 사유 <span style={{ color: 'var(--color-gray-400)', fontSize: '12px' }}>(선택)</span>
                  </label>
                  <textarea
                    value={monthlyFormData.reason}
                    onChange={e => setMonthlyFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="조정 사유를 입력하세요 (감사 로그에 기록됨)"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '4px',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* 상태 메시지 */}
        {saveStatus === 'error' && errorMessage && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'var(--color-red-50)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--color-red-700)',
              fontSize: '13px',
            }}
          >
            <AlertCircle size={16} />
            {errorMessage}
          </div>
        )}

        {saveStatus === 'success' && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'var(--color-green-50)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--color-green-700)',
              fontSize: '13px',
            }}
          >
            <CheckCircle size={16} />
            저장 완료! 잔여 일수가 재계산되었습니다.
          </div>
        )}

        {/* 버튼 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '24px',
          }}
        >
          <button
            onClick={onClose}
            disabled={saveStatus === 'saving'}
            style={{
              padding: '10px 20px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            취소
          </button>
          <button
            onClick={activeTab === 'base' ? handleSave : handleMonthlyAdjustSave}
            disabled={saveStatus === 'saving' || saveStatus === 'success' || (activeTab === 'monthly' && loadingMonthly)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: saveStatus === 'saving' || saveStatus === 'success' || (activeTab === 'monthly' && loadingMonthly)
                ? 'var(--color-gray-300)'
                : 'var(--color-primary)',
              color: 'white',
              cursor: saveStatus === 'saving' || saveStatus === 'success' || (activeTab === 'monthly' && loadingMonthly) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {saveStatus === 'saving' ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                {activeTab === 'base' ? '저장 중...' : '조정 중...'}
              </>
            ) : saveStatus === 'success' ? (
              <>
                <CheckCircle size={16} />
                완료
              </>
            ) : (
              <>
                <Save size={16} />
                {activeTab === 'base' ? '저장' : '조정 적용'}
              </>
            )}
          </button>
        </div>

        {/* 안내 문구 */}
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'var(--color-yellow-50)',
            borderRadius: '6px',
            fontSize: '12px',
            color: 'var(--color-yellow-700)',
          }}
        >
          {activeTab === 'base' ? (
            <>
              <strong>안내:</strong> 이월/선사용/적용기간만 수정 가능합니다.
              발생은 정책에 따라 자동 산정되며 수기 수정 대상이 아닙니다.
              사용/잔여는 저장 시 자동으로 재계산됩니다.
            </>
          ) : (
            <>
              <strong>안내:</strong> 특정 월의 사용량을 조정합니다.
              연차 원장(leaveEntitlements)이 조정되며, 잔여 일수가 자동 재계산됩니다.
            </>
          )}
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}

export default LeaveBalanceEditModal;
